import React, { useEffect, useMemo, useState } from "react";
import { Info, MessageCircle, Paperclip, Send, Upload, X } from "lucide-react";

// Envoi WhatsApp fiche : via POST /mail/bulk {template_id, lead_ids, channel:"whatsapp"}.
// Le template est requis (exigé par /mail/bulk) ; le texte reste éditable et est
// transmis avec la demande (body).
const CrmV2WhatsAppComposerModal = ({ open, lead, templates = [], onClose, onSend }) => {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);

  const selectedTemplate = useMemo(() => templates.find((item) => String(item.id) === String(templateId)), [templates, templateId]);
  const characters = body.length;

  useEffect(() => {
    if (!open) return;
    setTo(lead?.mobile || lead?.phone || "");
    setBody("");
    setTemplateId("");
    setTemplateOpen(false);
    setSending(false);
    setLocalAttachments([]);
  }, [open, lead]);

  // Pré-sélection auto quand un seul template WhatsApp existe (chargé en async après ouverture)
  useEffect(() => {
    if (!open || templateId || body || templates.length !== 1) return;
    setTemplateId(String(templates[0].id));
    setBody(templates[0].body_text || templates[0].body_html || "");
  }, [open, templates, templateId, body]);

  if (!open) return null;

  const applyTemplate = (template) => {
    setTemplateId(String(template.id));
    setBody(template.body_text || template.body_html || "");
    setTemplateOpen(false);
  };

  const submit = async () => {
    if (!to.trim() || !templateId || sending) return;
    setSending(true);
    try {
      await onSend?.({ channel: "whatsapp", template_id: parseInt(templateId, 10), to: to.trim(), body: body.trim(), attachment_files: localAttachments });
      onClose();
    } finally {
      setSending(false);
    }
  };

  const addLocalAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) setLocalAttachments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const removeLocalAttachment = (file) => {
    setLocalAttachments((current) => current.filter((item) => item !== file));
  };

  const canSend = !!to.trim() && !!templateId && !sending;

  return (
    <>
      <div style={styles.backdrop} role="dialog" aria-modal="true" aria-label="Send WhatsApp message">
        <div style={styles.dialog}>
          <div style={styles.header}>
            <strong>Send WhatsApp message</strong>
            <button type="button" onClick={onClose} style={styles.close} aria-label="Close"><X size={21} /></button>
          </div>
          <div style={styles.notice}>
            <strong>Template requis :</strong> sélectionnez un template (requis pour l'envoi). Vous pouvez retoucher le texte — il est transmis avec la demande.
          </div>
          <div style={styles.body}>
            <div style={styles.recipientRow}>
              <label>Recipient</label>
              <input value={to} onChange={(event) => setTo(event.target.value)} placeholder="e.g. +237 655 230 405" style={styles.input} />
            </div>
            <label style={styles.messageLabel}>Message</label>
            <div style={styles.messageBox}>
              <textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Write your message or select a template below..." style={styles.textarea} />
              <MessageCircle size={16} style={styles.messageIcon} />
            </div>
            <div style={styles.counter}>
              <span>{characters} characters <Info size={14} /></span>
              <span>{selectedTemplate ? `Template : ${selectedTemplate.name}` : "Aucun template sélectionné"}</span>
            </div>
            <div style={styles.templateBar}>
              <span>Load template</span>
              {templateOpen ? (
                <select autoFocus value={templateId} onChange={(event) => { const template = templates.find((item) => String(item.id) === String(event.target.value)); if (template) applyTemplate(template); }} style={styles.templateSelect}>
                  <option value="">Select a message template...</option>
                  {templates.slice(0, 8).map((template, i) => <option key={template.id ?? `tpl-${i}`} value={template.id}>{template.name}</option>)}
                </select>
              ) : (
                <button type="button" onClick={() => setTemplateOpen(true)} style={styles.templateButton}>{selectedTemplate?.name || "Select a message template"}</button>
              )}
            </div>
            {templates.length === 0 && (
              <div style={styles.noTemplate}>No WhatsApp templates found. Create one in CRM Settings → Message templates (channel WhatsApp).</div>
            )}
            <div style={styles.attachRow}>
              <label htmlFor="crm-wa-local-attachment" style={styles.uploadButton}><Upload size={14} /> Attach a file</label>
              <input id="crm-wa-local-attachment" type="file" multiple onChange={addLocalAttachments} style={styles.fileInput} />
              {localAttachments.map((file) => <span key={`${file.name}-${file.size}-${file.lastModified}`} style={styles.localAttachment}><Paperclip size={13} /><span>{file.name}</span><button type="button" onClick={() => removeLocalAttachment(file)} style={styles.removeAttachment} aria-label={`Remove ${file.name}`}><X size={13} /></button></span>)}
            </div>
            <div style={styles.filesHint}>Les fichiers sont uploadés puis liés au lead avant envoi (comme en email).</div>
          </div>
          <div style={styles.footer}>
            <button type="button" onClick={submit} disabled={!canSend} style={styles.primaryButton}><Send size={14} /> {sending ? "SENDING..." : "SEND WHATSAPP"}</button>
            <button type="button" onClick={onClose} style={styles.closeButton}>CLOSE</button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  backdrop: { position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,.48)" },
  hiddenLayer: { visibility: "hidden", pointerEvents: "none" },
  dialog: { width: "min(980px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "#fff", boxShadow: "0 16px 45px rgba(0,0,0,.3)", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif', color: "#17202a" },
  header: { minHeight: 54, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderBottom: "1px solid #d9dee3", fontSize: 16 },
  close: { border: 0, background: "transparent", color: "#6c757d", cursor: "pointer", display: "inline-flex", padding: 5 },
  notice: { padding: "15px 20px", background: "#f8d7da", borderBottom: "1px solid #f1b0b7", color: "#842029", fontSize: 14, textAlign: "center" },
  body: { overflowY: "auto", padding: "28px 32px 24px" },
  recipientRow: { display: "grid", gridTemplateColumns: "80px minmax(0, 1fr)", gap: 14, alignItems: "center", fontSize: 14 },
  input: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #adb5bd", outline: 0, padding: "8px 4px", fontSize: 14, color: "#343a40" },
  messageLabel: { display: "block", marginTop: 26, marginBottom: 10, fontSize: 14, fontWeight: 600 },
  messageBox: { position: "relative", borderBottom: "1px solid #6c757d" },
  textarea: { width: "100%", minHeight: 150, boxSizing: "border-box", border: 0, outline: 0, resize: "vertical", padding: "10px 28px 10px 0", fontFamily: "inherit", fontSize: 15, lineHeight: 1.6 },
  messageIcon: { position: "absolute", right: 5, bottom: 10, color: "#714b67" },
  counter: { display: "flex", justifyContent: "space-between", alignItems: "center", color: "#5c6f82", fontSize: 13, paddingTop: 6 },
  templateBar: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 24, paddingTop: 14, borderTop: "1px solid #e5e7eb", color: "#495057", fontSize: 13 },
  templateButton: { border: 0, borderBottom: "1px solid #adb5bd", background: "#fff", color: "#714b67", padding: "7px 3px", cursor: "pointer", fontSize: 13, minWidth: 240, textAlign: "left" },
  noTemplate: { marginTop: 12, fontSize: 12.5, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px" },
  filesHint: { marginTop: 8, fontSize: 11.5, color: "#94a3b8" },
  templateSelect: { minWidth: 280, border: "1px solid #adb5bd", background: "#fff", color: "#343a40", padding: "8px 10px", fontSize: 13 },
  attachRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 14 },
  uploadButton: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 10px", border: "1px solid #adb5bd", background: "#fff", color: "#495057", cursor: "pointer", fontSize: 12 },
  fileInput: { display: "none" },
  localAttachment: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px", border: "1px solid #b8d2df", background: "#eef7fb", color: "#315b6d", fontSize: 12 },
  removeAttachment: { display: "inline-flex", alignItems: "center", border: 0, background: "transparent", color: "#714b67", cursor: "pointer", padding: 0 },
  footer: { display: "flex", alignItems: "center", gap: 16, minHeight: 62, padding: "0 24px", borderTop: "1px solid #d9dee3" },
  primaryButton: { display: "inline-flex", alignItems: "center", gap: 6, border: 0, background: "#789bad", color: "#fff", padding: "10px 14px", fontSize: 12, cursor: "pointer" },
  closeButton: { border: 0, background: "transparent", color: "#714b67", padding: 0, fontSize: 12, cursor: "pointer" },
};

export default CrmV2WhatsAppComposerModal;
